// types/course.ts
//
// Courses V1, as the backend serves it: ora-workspace/docs/courses/
// FRONTEND-INTEGRATION.md (Kevin, 2026-10-22, version 1). The shapes below
// are that guide's section 2, verbatim in meaning; the helpers at the end are
// the frontend's own. ASSESSMENT steps (module quizzes, midterms, finals)
// are ORA-1068's additive shapes, from its design (ASSESSMENTS-DESIGN.md);
// CONVERSATION steps (a practice conversation with a simulated client) are
// ORA-1055's, from PRACTICE-DESIGN.md.
//
// A course is a prescriptive path: modules, each an ordered list of steps,
// each step one activity of one kind. Progress is per step and explicit: the
// client starts a step, studies it through the existing surfaces, and asks
// the server to complete it; the server checks the rule and answers.

export type CourseStepKind =
  | 'QBANK'
  | 'FLASHCARDS'
  | 'VIDEO'
  | 'ARTICLE'
  | 'ASSESSMENT'
  | 'NARRATION'
  | 'CONVERSATION';

export type CourseStepStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

export type CourseContentType =
  'VIGNETTE_V2' | 'FLASHCARD_V2' | 'VIDEO' | 'ARTICLE_V2';

export interface CourseStepProgress {
  status: CourseStepStatus;
  /** ISO. */
  startedAt: string | null;
  /** ISO. */
  completedAt: string | null;
  /** QBANK only: the latest block for this step. */
  blockId: string | null;
}

export interface CourseStepSummary {
  id: string;
  /** 1-based within the module. */
  ord: number;
  kind: CourseStepKind;
  /** The author's label; may be null. */
  title: string | null;
  itemCount: number;
  progress: CourseStepProgress;
  /**
   * Author metadata, additive and optional (e.g. `hook`, a line shown above
   * the step's first item). Unknown keys are ignored.
   */
  meta?: Record<string, unknown> | null;
  /** NARRATION steps only: a welcome, a bridge between topics, or a recap. */
  narrationRole?: 'intro' | 'transition' | 'recap';
  /** ASSESSMENT steps only: the self-assessment the step points at. */
  assessment?: CourseAssessment | null;
}

export type CourseAssessmentKind = 'quiz' | 'midterm' | 'final';

/** A finished attempt's score. */
export interface CourseAssessmentScore {
  correct: number;
  total: number;
  percent: number;
  points: number;
  maxPoints: number;
}

export interface CourseAssessmentAttempt {
  id: string;
  status: 'in_progress' | 'completed';
  /** ISO. */
  startedAt: string;
  /** ISO. */
  completedAt: string | null;
  /** Non-null only when completed. */
  score: CourseAssessmentScore | null;
}

/**
 * A module quiz, midterm or final (ORA-1068): one timed self-assessment with
 * one attempt, taken through the self-assessment pages. Rationales and the
 * score come after the attempt is submitted.
 */
export interface CourseAssessment {
  selfAssessmentId: string;
  /** The self-assessment's slug, for the `/self-assessments/:slug` routes. */
  slug: string;
  kind: CourseAssessmentKind;
  title: string;
  questionCount: number;
  timeLimitMinutes: number;
  maxPoints: number;
  /** Null until the student starts it. */
  attempt: CourseAssessmentAttempt | null;
}

export interface CourseStepItem {
  /** 1-based within the step. */
  ord: number;
  contentType: CourseContentType;
  contentId: string;
  /** Videos and articles only. */
  title: string | null;
  /** Videos only. */
  durationSeconds: number | null;
}

export interface CourseStepDetail extends CourseStepSummary {
  items: CourseStepItem[];
}

export interface CourseModuleSummary {
  id: string;
  /** 1-based within the course. */
  ord: number;
  slug: string;
  title: string;
  description: string | null;
  stepCount: number;
  completedStepCount: number;
  steps: CourseStepSummary[];
}

export interface CourseReviewCounts {
  dueQuestionCount: number;
  dueFlashcardCount: number;
}

export interface CourseModuleDetail extends Omit<CourseModuleSummary, 'steps'> {
  steps: CourseStepDetail[];
  review: CourseReviewCounts;
}

export interface CourseResumePointer {
  moduleId: string;
  stepId: string;
}

export interface CourseSummary {
  id: string;
  slug: string;
  /** Matches `UserExam.id` in `GET /exams`. */
  examId: string;
  title: string;
  description: string | null;
  moduleCount: number;
  stepCount: number;
  completedStepCount: number;
  /** The first step in course order not COMPLETED; null when the course is done. */
  resume: CourseResumePointer | null;
}

export interface CourseDetail extends CourseSummary {
  modules: CourseModuleSummary[];
  review: CourseReviewCounts;
}

export type CourseStepLaunch =
  | { kind: 'QBANK'; blockId: string; isSubmitted: boolean }
  | { kind: 'FLASHCARDS'; flashcardIds: string[] }
  | { kind: 'VIDEO'; videoIds: string[] }
  | { kind: 'ARTICLE'; articleIds: string[] }
  | { kind: 'ASSESSMENT'; assessment: CourseAssessment }
  | {
      /**
       * A module's intro or recap, spoken by the teacher. `text` breaks
       * paragraphs with newlines; `audio` is null when there is no voice.
       * The step has no items, and `complete` always grants it.
       */
      kind: 'NARRATION';
      /** A welcome, a recap, or a bridge between two topics. */
      role: 'intro' | 'recap' | 'transition';
      text: string;
      audio: { url: string; durationSeconds: number } | null;
    }
  | {
      /**
       * A practice conversation: the student plays the counselor, the AI a
       * simulated client (PRACTICE-DESIGN.md). No items; `complete` grants it
       * once a finished session has at least `minTurns` student turns.
       */
      kind: 'CONVERSATION';
      practice: CoursePractice;
    };

/** How the server codes a student turn (MITI 4.2). */
export type CoursePracticeCode =
  | 'open_question'
  | 'closed_question'
  | 'simple_reflection'
  | 'complex_reflection'
  | 'affirmation'
  | 'summary'
  | 'fixing'
  | 'other';

export type CoursePracticeTalk = 'change' | 'sustain' | 'neutral';

export interface CoursePracticeTurn {
  /** 0 is the client's opening line. */
  index: number;
  speaker: 'client' | 'student';
  text: string;
  /** Student turns only; [] for the client. */
  codes: CoursePracticeCode[];
  /** Client turns only. */
  talk: CoursePracticeTalk | null;
  /** Client turns: the relative API path of the turn's audio route. */
  audioPath: string | null;
}

export interface CoursePracticeGoal {
  id: string;
  label: string;
  met: boolean;
  current: number;
  target: number;
}

export interface CoursePracticeCoach {
  turnIndex: number;
  message: string;
  betterLine: string | null;
  reason: 'fixing' | 'question_streak';
}

export interface CoursePracticeStats {
  counts: Record<
    | 'openQuestions'
    | 'closedQuestions'
    | 'simpleReflections'
    | 'complexReflections'
    | 'affirmations'
    | 'summaries'
    | 'fixing',
    number
  >;
  /** Reflections per question; null when there were no questions. */
  reflectionToQuestionRatio: number | null;
  complexReflectionPercent: number | null;
  openQuestionPercent: number | null;
  /** The MITI thresholds the stats are read against. */
  miti: {
    reflectionToQuestion: { fair: number; good: number };
    complexReflectionPercent: { fair: number; good: number };
  };
  retriedTurns: number;
}

export interface CoursePracticeFeedback {
  summary: string;
  worked: Array<{ turnIndex: number; quote: string; why: string }>;
  retry: Array<{
    turnIndex: number;
    quote: string;
    betterLine: string;
    /** The lesson step to review; null when the module has none. */
    lesson: { stepId: string; title: string } | null;
  }>;
}

export interface CoursePracticeSession {
  id: string;
  status: 'active' | 'finished';
  turns: CoursePracticeTurn[];
  goals: CoursePracticeGoal[];
  studentTurnCount: number;
  /** At least `minTurns` student turns: End Conversation is allowed. */
  canFinish: boolean;
  /** A nudge on the latest student turn, which can then be retried. */
  coach: CoursePracticeCoach | null;
  /** Live counts while active; final when finished. */
  stats: CoursePracticeStats | null;
  feedback: CoursePracticeFeedback | null;
  /** ISO. */
  startedAt: string;
  /** ISO. */
  finishedAt: string | null;
}

export interface CoursePractice {
  /** Never the client's character, reveal or voice instructions. */
  scenario: {
    personaName: string;
    personaAge: number;
    brief: string;
    goal: string;
    goals: Array<{ id: string; label: string }>;
    minTurns: number;
    maxTurns: number;
  };
  /** The latest ACTIVE session. */
  activeSession: CoursePracticeSession | null;
  lastFinishedSession: CoursePracticeSession | null;
  finishedCount: number;
}

export interface CourseReviewLaunch {
  /** Null when no questions are due. */
  blockId: string | null;
  /** Empty when no cards are due. */
  flashcardIds: string[];
}

/** What `complete` says is still missing when it refuses (409 STEP_INCOMPLETE). */
export interface CourseStepMissing {
  flashcardIds?: string[];
  videoIds?: string[];
  /** VIDEO: live questions on the step's videos with no answer yet. */
  liveQuestionCount?: number;
  articleIds?: string[];
  blockId?: string;
  isSubmitted?: boolean;
  /** ASSESSMENT: the attempt is not submitted yet. */
  kind?: 'ASSESSMENT' | 'CONVERSATION';
  attemptStatus?: 'not_started' | 'in_progress';
  /** CONVERSATION: no finished session of `minTurns` yet. */
  finishedSessions?: number;
}

export interface CourseStepStartResult {
  step: CourseStepSummary;
  launch: CourseStepLaunch;
}

export interface CourseStepCompleteResult {
  step: CourseStepSummary;
  module: { id: string; completedStepCount: number; stepCount: number };
  course: {
    id: string;
    completedStepCount: number;
    stepCount: number;
    resume: CourseResumePointer | null;
  };
}

/** What the rail calls a step that has no author title. */
export const COURSE_STEP_KIND_LABELS: Record<CourseStepKind, string> = {
  QBANK: 'Questions',
  FLASHCARDS: 'Flashcards',
  VIDEO: 'Video',
  ARTICLE: 'Reading',
  ASSESSMENT: 'Assessment',
  NARRATION: 'Narration',
  CONVERSATION: 'Practice conversation',
};

export const courseStepLabel = (
  step: Pick<CourseStepSummary, 'kind' | 'title'>
): string => step.title?.trim() || COURSE_STEP_KIND_LABELS[step.kind];

/**
 * What the rail calls a step: a welcome is always "Welcome" and a recap
 * always "Recap", whatever the author titled them (the body keeps the
 * title); every other step reads its label.
 */
export const courseRailStepLabel = (
  step: Pick<CourseStepSummary, 'kind' | 'title' | 'narrationRole'>
): string => {
  if (step.kind === 'NARRATION' && step.narrationRole === 'intro')
    return 'Welcome';
  if (step.kind === 'NARRATION' && step.narrationRole === 'recap')
    return 'Recap';
  return courseStepLabel(step);
};

/** Points keep one decimal when they have one (77 items for 80 points). */
export const formatCoursePoints = (points: number): string =>
  Number.isInteger(points) ? String(points) : points.toFixed(1);
